import './order-print.css';
import { Button, Spin } from 'antd';
import { CloseOutlined, PrinterOutlined } from '@ant-design/icons';
import { useEffect, useRef, useState } from 'react';
import { serverFetch, fileUrl } from '@server';
import { showError, fmtDate, fmtMoney } from '@extensions/utils';
import propis from '@extensions/propis';
import ResizableDrawer from '@controls/resizable-drawer/resizable-drawer';

// Стили для печати в iframe. Дублируют правила .order-print из order-print.css — держать синхронно.
const PRINT_CSS = `
@page { margin: 15mm; }
body { margin: 0; }
.order-print { font-family: Arial, sans-serif; font-size: 12pt; color: #000; }
.order-print table { width: 100%; border-collapse: collapse; }
.order-print .op-head td { vertical-align: top; padding: 20px 0; }
.order-print .op-head-text { width: 68%; }
.order-print .op-warn { text-align: justify; font-size: 11pt; }
.order-print .op-head-logo { width: 32%; text-align: center; }
.order-print .op-head-logo img { max-width: 70%; max-height: 30mm; }
.order-print .op-bank td { border: 1px solid #000; padding: 2px; vertical-align: top; }
.order-print .op-bank-name { width: 105mm; }
.order-print .op-bank-value { min-height: 10mm; }
.order-print .op-bank-label { font-size: 10pt; }
.order-print .op-bank-cell { width: 25mm; }
.order-print .op-bank-accounts { width: 60mm; vertical-align: top; }
.order-print .op-title { font-weight: bold; font-size: 25pt; padding-left: 5px; margin: 10px 0; }
.order-print .op-line { background: #000; height: 2px; width: 100%; }
.order-print .op-parties { margin: 8px 0; }
.order-print .op-parties td { vertical-align: top; }
.order-print .op-party-label { width: 30mm; padding-left: 2px; }
.order-print .op-party-name { font-weight: bold; padding-left: 2px; }
.order-print .op-party-sub { font-weight: normal; }
.order-print .op-items { margin-top: 8px; }
.order-print .op-items th, .order-print .op-items td { border: 1px solid #000; padding: 2px; }
.order-print .op-items .op-c { text-align: center; }
.order-print .op-totals { margin-top: 4px; }
.order-print .op-totals td { padding: 1px; }
.order-print .op-totals .op-l { font-weight: bold; text-align: right; width: 37mm; }
.order-print .op-totals .op-v { font-weight: bold; text-align: center; width: 27mm; }
.order-print .op-propis { margin: 10px 0; }
.order-print .op-cond { font-size: 10pt; margin-top: 10px; }
.order-print .op-sign { margin-top: 16px; }
`;

const waitImages = (doc) => Promise.all(Array.from(doc.images || []).map((img) => {
    if (img.complete) return Promise.resolve();
    return new Promise((resolve) => {
        img.onload = resolve;
        img.onerror = resolve;
        setTimeout(resolve, 1500);
    });
}));

// Печать содержимого счёта через скрытый iframe
const printHtml = (html) => {
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(`<html><head><meta charset="utf-8"><style>${PRINT_CSS}</style></head><body>${html}</body></html>`);
    doc.close();

    const cleanup = () => {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    };

    waitImages(doc).then(() => {
        const w = iframe.contentWindow;
        w.onafterprint = cleanup;
        w.focus();
        w.print();
        setTimeout(cleanup, 1000);
    });
};

// Drawer «Счёт» по заказу
const OrderPrint = ({ order, onClose }) => {
    const [company, setCompany] = useState(null);
    const [items, setItems] = useState(null);
    const [contragent, setContragent] = useState(null);
    const [loading, setLoading] = useState(true);
    const printRef = useRef(null);

    useEffect(() => {
        const reqs = [
            serverFetch('company/current').then(setCompany),
            serverFetch(`order/${order.id}/items`).then(setItems),
        ];
        if (order.contragentId) {
            reqs.push(serverFetch(`contragent/${order.contragentId}`).then(setContragent));
        }
        Promise.all(reqs)
            .catch(showError)
            .finally(() => setLoading(false));
    }, [order.id, order.contragentId]);

    // Пока открыт drawer счёта, печать страницы (Ctrl+P) выводит только счёт
    useEffect(() => {
        document.body.classList.add('order-print-mode');
        return () => document.body.classList.remove('order-print-mode');
    }, []);

    const onPrint = () => {
        if (printRef.current) printHtml(printRef.current.outerHTML);
    };

    const ready = !loading && company && items && (!order.contragentId || contragent);
    const total = (items ?? []).reduce((s, i) => s + (Number(i.syma) || 0), 0);
    const supplierName = company ? (company.fullName || company.name) : '';
    const buyerName = contragent ? (contragent.fullName || contragent.name) : '';

    return (
        <ResizableDrawer
            open
            storageKey="orderPrintDrawerWidth"
            defaultWidth={900}
            title="Счёт"
            closeIcon={null}
            extra={<Button type="text" size="small" icon={<CloseOutlined />} onClick={onClose} />}
            onClose={onClose}
            destroyOnClose
            className="resizable-drawer order-print-drawer"
            rootClassName="order-print-root"
            footer={(
                <div className="order-print-footer">
                    <Button type="primary" icon={<PrinterOutlined />} disabled={!ready} onClick={onPrint}>Печать</Button>
                    <Button onClick={onClose}>Закрыть</Button>
                </div>
            )}
        >
            {ready ? (
                <div className="order-print" ref={printRef}>
                    <table className="op-head">
                        <tbody>
                            <tr>
                                <td className="op-head-text">
                                    <div className="op-warn">
                                        Внимание! Оплата данного счета означает согласие с условиями поставки товара и услуг.
                                        Счет действителен в течение 5 (пяти) банковских дней, не считая дня выписки счета.
                                    </div>
                                </td>
                                <td className="op-head-logo">
                                    {company.logoFileId && <img src={fileUrl(company.logoFileId, true)} alt="" />}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <table className="op-bank">
                        <tbody>
                            <tr>
                                <td colSpan="2" rowSpan="2" className="op-bank-name">
                                    <div className="op-bank-value">{company.bank}</div>
                                    <div className="op-bank-label">Банк получателя</div>
                                </td>
                                <td className="op-bank-cell">БИК</td>
                                <td rowSpan="2" className="op-bank-accounts">
                                    <div>{company.bik}</div>
                                    <div>{company.ks}</div>
                                </td>
                            </tr>
                            <tr>
                                <td className="op-bank-cell">Сч. №</td>
                            </tr>
                            <tr>
                                <td className="op-bank-cell">ИНН {company.inn}</td>
                                <td className="op-bank-cell">КПП {company.kpp}</td>
                                <td rowSpan="2" className="op-bank-cell">Сч. №</td>
                                <td rowSpan="2" className="op-bank-accounts">{company.rs}</td>
                            </tr>
                            <tr>
                                <td colSpan="2" className="op-bank-name">
                                    <div className="op-bank-value">{supplierName}</div>
                                    <div className="op-bank-label">Получатель</div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <div className="op-title">Счет № {order.docNum || order.id} от {fmtDate(order.docDate)}</div>
                    <div className="op-line" />
                    <table className="op-parties">
                        <tbody>
                            <tr>
                                <td className="op-party-label">Поставщик:</td>
                                <td>
                                    <div className="op-party-name">
                                        {supplierName} ИНН/КПП {company.inn}/{company.kpp}<br />
                                        <span className="op-party-sub">
                                            {company.address}
                                            {company.phone && <><br />тел.: {company.phone}</>}
                                        </span>
                                    </div>
                                </td>
                            </tr>
                            {contragent && (
                                <tr>
                                    <td className="op-party-label">Покупатель:</td>
                                    <td>
                                        <div className="op-party-name">
                                            {buyerName}{contragent.inn ? `, ИНН ${contragent.inn}` : ''}<br />
                                            <span className="op-party-sub">
                                                {contragent.address}
                                                {contragent.phone && <><br />тел.: {contragent.phone}</>}
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                    <table className="op-items">
                        <thead>
                            <tr>
                                <th style={{ width: '13mm' }}>№</th>
                                <th>Товары (работы, услуги)</th>
                                <th style={{ width: '20mm' }}>Кол-во</th>
                                <th style={{ width: '17mm' }}>Ед.</th>
                                <th style={{ width: '27mm' }}>Цена</th>
                                <th style={{ width: '27mm' }}>Сумма</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((it, idx) => (
                                <tr key={it.id ?? idx}>
                                    <td className="op-c">{idx + 1}</td>
                                    <td>{it.productName}</td>
                                    <td className="op-c">{it.qty}</td>
                                    <td className="op-c">{it.unit}</td>
                                    <td className="op-c">{fmtMoney(it.price)}</td>
                                    <td className="op-c">{fmtMoney(it.syma)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <table className="op-totals">
                        <tbody>
                            <tr>
                                <td />
                                <td className="op-l">Итого:</td>
                                <td className="op-v">{fmtMoney(total)}</td>
                            </tr>
                            <tr>
                                <td />
                                <td className="op-l">Итого НДС:</td>
                                <td className="op-v">Без НДС</td>
                            </tr>
                            <tr>
                                <td />
                                <td className="op-l">Всего к оплате:</td>
                                <td className="op-v">{fmtMoney(total)}</td>
                            </tr>
                        </tbody>
                    </table>
                    <div className="op-propis">
                        Всего наименований {items.length} на сумму {fmtMoney(total)} рублей.<br />
                        {propis(total)}
                    </div>
                    <div className="op-line" />
                    <div className="op-cond">
                        1. Счет действителен в течение 5 (пяти) банковских дней, не считая дня выписки счета.
                        В случае нарушения срока оплаты сохранение цены на товар и наличие товара на складе НЕ ГАРАНТИРУЕТСЯ.<br />
                        2. Оплата данного счета означает согласие с условиями изложенными в п.1
                    </div>
                    {company.signFileId && (
                        <img className="op-sign" src={fileUrl(company.signFileId, true)} alt="" />
                    )}
                </div>
            ) : (
                <div style={{ textAlign: 'center', padding: '40px 0' }}><Spin /></div>
            )}
        </ResizableDrawer>
    );
};

export default OrderPrint;
